/**
 * mapModel — modelo PURO do Editor de galpão do WMS (M1 r2, interação direta
 * na r3, fundação de RACKS em 02/09/2026).
 *
 * A física do mundo veio do protótipo do dono (`ensaio-enderacamento/
 * minimap.html`), mas em funções puras, sem DOM e sem estado global. Toda
 * operação de edição recebe o layout, trabalha numa CÓPIA profunda, valida o
 * mundo inteiro e devolve `{ ok, model, error }` — snapshot/rollback (mutação
 * inválida devolve o modelo original intacto + a mensagem do erro).
 *
 * ── NÃO EXISTE MAIS RUA (decisão do dono, 02/09/2026) ───────────────────────
 * O que era uma "rua" com dois porta-pallets (lado ímpar/par) virou: cada
 * porta-pallet é um RACK independente, identificado por um prefixo opaco de
 * 1–2 letras (`A`, `B`…), com blocos numerados em SEQUÊNCIA (1, 2, 3…) ao longo
 * do traçado, que o dono distribui livremente pelo galpão. No desenho um rack é
 * um CORREDOR (a lista de células do traçado — onde a empilhadeira anda) com os
 * blocos de UM lado só (`baySide`). Não há paridade, não há "lado ímpar/par".
 *
 * Shape do layout (`wms_map_layouts`, chaves EXATAS — `rack` é o vínculo com
 * `wms_locations.rack` e `blocks[].bay` com `wms_locations.bay`; o `code` da
 * fixa faz o vínculo com o endereço literal real):
 * {
 *   grid:   { w, h, cell },
 *   areas:  [{ id, type, x1, y1, x2, y2, label? }],
 *   racks:  [{ id, areaId, name, rack, orientation, baySide, start, end, dir,
 *              sequence: [{ x, y, bayId }],
 *              params: { levels, maxLevels, levelHeight, positions, blockWidth } }],
 *   blocks: [{ id, rackId, x, y, bay, width, maxLevels }],
 *   fixed:  [{ id, code, x, y }],
 * }
 *
 * Layout ANTIGO salvo no banco (`streets[]`, blocos com `streetId`) é ignorado
 * com segurança pela `NormalizeLayout` — o dono recadastra o galpão do zero.
 *
 * Diferenças deliberadas em relação ao protótipo:
 * - `rack.blockIds` NÃO existe — a associação bloco↔rack é derivada de
 *   `blocks[].rackId` e de `sequence[].bayId`.
 * - `block.levels[].slots[]` NÃO existe — andar/posição/ocupação são dados
 *   REAIS (wms_locations / módulos M3+), nunca duplicados no layout.
 *
 * Regras da 4ª rodada (10/08 — PREVALECEM):
 * - Staging SOLTO: staging pode viver FORA de qualquer área (transferência
 *   entre setores) ou DENTRO de uma base (staging do processo do setor). A
 *   associação à área é DINÂMICA: deriva da geometria a cada movimento.
 * - `fixed` (DCA/STG-DCA/FAB/INC): 1 célula livre por fixa, dentro OU fora de
 *   área, `code` único — sempre corresponde a um endereço literal REAL.
 * - Excluir rack/bloco no DESENHO só acontece DEPOIS de o backend soft-deletar
 *   os endereços reais (fluxo do WarehouseMap, com senha); áreas seguem livres,
 *   mas não podem levar racks junto.
 */
import { DEFAULT_RACK_WEIGHTS_BY_LEVEL } from '../../../constants/warehouseLocations';

/** Margem do desenho (px) — espaço das réguas de coordenadas. */
export const MAP_MARGIN = 46;

/** Limites do grid (mesmos inputs do protótipo). */
export const GRID_LIMITS = { minSize: 1, maxSize: 300, minCell: 14, maxCell: 80 };

/** Prefixo do rack: 1 ou 2 letras maiúsculas (a mesma gramática do backend). */
export const RACK_PREFIX_PATTERN = /^[A-Z]{1,2}$/;

/** Limites do generate-rack real (PRD §4: A tem 1 dígito, o vão tem 2). */
export const RACK_REAL_MAX_LEVELS = 9;
export const RACK_REAL_MAX_BAYS = 99;

/**
 * A1 = térreo. O pallet apoia no CHÃO, não na longarina — não existe limite de
 * peso a declarar (o backend RECUSA peso no A1). O formulário esconde o A1 da
 * gestão de pesos e o payload nunca o manda.
 */
export const GROUND_LEVEL = 1;

/**
 * Metadados por tipo de área: cores de desenho nos DOIS temas do app
 * (`fill`/`stroke` no claro, `fillDark`/`strokeDark` no escuro — os fills são
 * translúcidos de propósito, deixam o grid respirar), `stackable` (staging
 * pode viver DENTRO de uma área base) e `allowRack` (racks/blocos só existem
 * dentro de Logística, Doca ou Produção). Rótulos PT-BR ficam no dicionário
 * (`wms.map.area_type.{type}` / chips da paleta em `wms.map.palette.{type}`).
 */
export const AREA_TYPES = {
    logistica: {
        fill: 'rgba(37, 99, 235, 0.12)',
        stroke: '#2563eb',
        fillDark: 'rgba(96, 165, 250, 0.14)',
        strokeDark: '#60a5fa',
        stackable: false,
        allowRack: true,
    },
    doca: {
        fill: 'rgba(217, 119, 6, 0.16)',
        stroke: '#d97706',
        fillDark: 'rgba(251, 191, 36, 0.15)',
        strokeDark: '#fbbf24',
        stackable: false,
        allowRack: true,
    },
    staging: {
        fill: 'rgba(22, 163, 74, 0.15)',
        stroke: '#16a34a',
        fillDark: 'rgba(74, 222, 128, 0.15)',
        strokeDark: '#4ade80',
        stackable: true,
        allowRack: false,
    },
    producao: {
        fill: 'rgba(147, 51, 234, 0.13)',
        stroke: '#9333ea',
        fillDark: 'rgba(192, 132, 252, 0.15)',
        strokeDark: '#c084fc',
        stackable: false,
        allowRack: true,
    },
    buffer: {
        fill: 'rgba(107, 114, 128, 0.16)',
        stroke: '#6b7280',
        fillDark: 'rgba(156, 163, 175, 0.18)',
        strokeDark: '#9ca3af',
        stackable: false,
        allowRack: false,
    },
    outro: {
        fill: 'rgba(148, 163, 184, 0.16)',
        stroke: '#64748b',
        fillDark: 'rgba(148, 163, 184, 0.18)',
        strokeDark: '#94a3b8',
        stackable: false,
        allowRack: false,
    },
};

/** Tamanho default da área criada ao soltar o chip da paleta (células). */
export const AREA_DROP_SIZE = { w: 4, h: 3 };

/**
 * Defaults do painel de rack (aberto DEPOIS dos dois cliques no mapa).
 *
 * A geometria vem do traçado (1º clique = início, 2º = fim; ver
 * `DeriveRackAxis`). `orientation` aqui é só SEMENTE do ghost que segue o
 * cursor ANTES do 1º clique; a orientação REAL é sempre derivada. `baySide` é
 * de que lado do corredor os blocos ficam (coagido ao eixo derivado). `bays`
 * = quantos blocos (null = herda o comprimento do traçado até a pessoa
 * digitar). levels/maxLevels/levelHeight/positions/blockWidth são os params do
 * DESENHO (contrato) e `weights` (kg por andar, indexado por andar−1; o A1 é
 * ignorado) NÃO entra no layout — alimenta o POST generate-rack que cria os
 * dados REAIS junto com o desenho.
 *
 * `blockWidth` é TAMBÉM a largura de vão que a geração grava em TODOS os blocos
 * do rack (M4-4): uma medida, perguntada uma vez. Bloco diferente se ajusta
 * DEPOIS (ficha do bloco / edição em massa).
 */
export const RACK_DRAFT_DEFAULTS = {
    name: '',
    rack: '',
    orientation: 'vertical',
    baySide: 'right',
    bays: null,
    levels: 3,
    maxLevels: 5,
    levelHeight: 2.5,
    positions: 2,
    blockWidth: 2.3,
    weights: DEFAULT_RACK_WEIGHTS_BY_LEVEL.slice(0, 3),
};

/**
 * Explicações das exclusões PROIBIDAS pelo teclado: bloco e fixa SEMPRE
 * correspondem a endereços reais — o mapa não os apaga por Delete (bloco tem o
 * fluxo com senha na ficha; fixa não tem exclusão).
 */
export const BLOCK_DELETE_BLOCKED_MSG =
    'Este bloco tem endereços reais vinculados — para excluí-lo use a ficha do bloco (duplo clique), que pede senha e só apaga se estiver vazio.';
export const FIXED_DELETE_BLOCKED_MSG =
    'Este endereço fixo existe de verdade (código real) — não dá para excluí-lo do mapa. Para tirar de uso, bloqueie-o na página de Localizações.';

function clampNumber(value, min, max) {
    return Math.max(min, Math.min(max, value));
}

function clampInt(raw, min, max, fallback) {
    if (raw === null || raw === undefined || String(raw).trim() === '') {
        return fallback;
    }
    const value = Number(raw);
    if (!Number.isFinite(value)) {
        return fallback;
    }
    return clampNumber(Math.round(value), min, max);
}

function isPlainObject(value) {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

/** Id novo (crypto.randomUUID com fallback, como o protótipo). */
export function Uid() {
    if (typeof window !== 'undefined' && typeof window.crypto?.randomUUID === 'function') {
        return window.crypto.randomUUID();
    }
    return `id-${Math.random().toString(36).slice(2, 10)}`;
}

/** Chave de célula "x,y" (mapas de ocupação). */
export function CellKey(x, y) {
    return `${x},${y}`;
}

/** Chave de bloco "RACK:bay" — a MESMA do overlay de status, do heatmap e da contagem. */
export function BlockKey(rack, bay) {
    return `${String(rack ?? '').toUpperCase()}:${Number(bay)}`;
}

/** Código bipável do bloco (`A-01`) — só apresentação; o backend é a fonte. */
export function FormatBayCode(rack, bay) {
    return `${String(rack ?? '').toUpperCase()}-${String(Number(bay) || 0).padStart(2, '0')}`;
}

/** Retângulo normalizado a partir de dois cantos {x,y}. */
export function NormRect(a, b) {
    return {
        x1: Math.min(a.x, b.x),
        x2: Math.max(a.x, b.x),
        y1: Math.min(a.y, b.y),
        y2: Math.max(a.y, b.y),
    };
}

/** Bounds normalizados a partir de coordenadas soltas. */
export function NormalizeBounds(x1, y1, x2, y2) {
    return {
        x1: Math.min(x1, x2),
        x2: Math.max(x1, x2),
        y1: Math.min(y1, y2),
        y2: Math.max(y1, y2),
    };
}

/** `inner` está totalmente contido em `outer`? */
export function RectInside(inner, outer) {
    return inner.x1 >= outer.x1 && inner.x2 <= outer.x2 && inner.y1 >= outer.y1 && inner.y2 <= outer.y2;
}

/** Os retângulos se tocam? */
export function RectsOverlap(a, b) {
    return !(a.x2 < b.x1 || a.x1 > b.x2 || a.y2 < b.y1 || a.y1 > b.y2);
}

/** Ponto {x,y} dentro do retângulo {x1,y1,x2,y2}? */
export function PointInsideRect(p, r) {
    return p.x >= r.x1 && p.x <= r.x2 && p.y >= r.y1 && p.y <= r.y2;
}

/** Célula dentro do grid do modelo? */
export function InsideGrid(x, y, grid) {
    return x >= 0 && y >= 0 && x < grid.w && y < grid.h;
}

/** Retângulo inteiro dentro do grid? */
export function RectInsideGrid(r, grid) {
    return r.x1 >= 0 && r.y1 >= 0 && r.x2 < grid.w && r.y2 < grid.h;
}

/** Metadados do tipo de área (fallback `outro`, como o protótipo). */
export function MetaForAreaType(type) {
    return AREA_TYPES[type] || AREA_TYPES.outro;
}

/** Metadados da área. */
export function MetaForArea(area) {
    return MetaForAreaType(area?.type);
}

/** Layout novo em folha: grid 30×30 vazio (default do protótipo). */
export function DefaultLayout() {
    return { grid: { w: 30, h: 30, cell: 28 }, areas: [], racks: [], blocks: [], fixed: [] };
}

/**
 * Prefixo de rack normalizado (`a` → `A`) ou null quando não é um prefixo
 * válido (1–2 letras). Normaliza em vez de recusar minúscula: quem digita `a`
 * quer o rack A.
 */
export function NormalizeRackPrefix(value) {
    const prefix = String(value ?? '')
        .trim()
        .toUpperCase();
    return RACK_PREFIX_PATTERN.test(prefix) ? prefix : null;
}

/**
 * Normaliza um layout vindo do wire (GET /warehouse/map-layout). `null`/lixo
 * → null (o chamador decide o default); objeto → garante grid clampado e os
 * cinco ramos com os tipos certos, preservando campos extras. Layout antigo
 * (ruas): `streets` é DESCARTADO e os blocos que apontavam para ruas
 * (`streetId`, sem `rackId` de rack existente) somem junto — o dono recadastra.
 */
export function NormalizeLayout(raw) {
    if (!isPlainObject(raw)) {
        return null;
    }
    const rest = { ...raw };
    delete rest.streets;
    const grid = isPlainObject(raw.grid) ? raw.grid : {};
    const racks = (Array.isArray(raw.racks) ? raw.racks.filter(isPlainObject) : []).map((rack) => ({
        ...rack,
        rack: NormalizeRackPrefix(rack.rack) ?? rack.rack,
        sequence: (Array.isArray(rack.sequence) ? rack.sequence.filter(isPlainObject) : []).map((item) => ({
            ...item,
            bayId: item.bayId ?? null,
        })),
    }));
    const rackIds = new Set(racks.map((rack) => rack.id));
    return {
        ...rest,
        grid: {
            w: clampInt(grid.w, GRID_LIMITS.minSize, GRID_LIMITS.maxSize, 30),
            h: clampInt(grid.h, GRID_LIMITS.minSize, GRID_LIMITS.maxSize, 30),
            cell: clampInt(grid.cell, GRID_LIMITS.minCell, GRID_LIMITS.maxCell, 28),
        },
        areas: Array.isArray(raw.areas) ? raw.areas.filter(isPlainObject) : [],
        racks,
        blocks: (Array.isArray(raw.blocks) ? raw.blocks.filter(isPlainObject) : []).filter((block) =>
            rackIds.has(block.rackId),
        ),
        fixed: Array.isArray(raw.fixed) ? raw.fixed.filter(isPlainObject) : [],
    };
}

/** Cópia profunda do layout (base do snapshot/rollback). */
export function CloneLayout(model) {
    return JSON.parse(JSON.stringify(model));
}

/** Área por id. */
export function GetAreaById(model, id) {
    return model.areas.find((area) => area.id === id) || null;
}

/** Rack por id. */
export function GetRackById(model, id) {
    return model.racks.find((rack) => rack.id === id) || null;
}

/** Rack pelo PREFIXO real (case-insensitive — o prefixo é o vínculo com o dado). */
export function FindRackByPrefix(model, prefix) {
    const wanted = NormalizeRackPrefix(prefix);
    if (!wanted) {
        return null;
    }
    return model.racks.find((rack) => NormalizeRackPrefix(rack.rack) === wanted) || null;
}

/** Bloco por id. */
export function GetBlockById(model, id) {
    return model.blocks.find((block) => block.id === id) || null;
}

/** Fixa por id. */
export function GetFixedById(model, id) {
    return (model.fixed || []).find((item) => item.id === id) || null;
}

/** Fixa pelo code real (case-insensitive — o code é o vínculo com o dado). */
export function FindFixedByCode(model, code) {
    const wanted = String(code || '')
        .trim()
        .toUpperCase();
    if (!wanted) {
        return null;
    }
    return (model.fixed || []).find((item) => String(item.code || '').toUpperCase() === wanted) || null;
}

/** Blocos de um rack (substitui o `blockIds` do protótipo). */
export function GetRackBlocks(model, rackId) {
    return model.blocks.filter((block) => block.rackId === rackId);
}

/** Todas as células ocupadas pelo rack (corredor + blocos). */
export function GetAllRackCells(model, rack) {
    const cells = [];
    for (const item of rack.sequence) {
        cells.push({ x: item.x, y: item.y });
    }
    for (const block of GetRackBlocks(model, rack.id)) {
        cells.push({ x: block.x, y: block.y });
    }
    return cells;
}

/** Bounds do corredor do rack (para o desenho do retângulo do corredor). */
export function RackBounds(rack) {
    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    for (const p of rack.sequence) {
        minX = Math.min(minX, p.x);
        minY = Math.min(minY, p.y);
        maxX = Math.max(maxX, p.x);
        maxY = Math.max(maxY, p.y);
    }
    return { minX, minY, maxX, maxY };
}

/**
 * Mapas de ocupação por célula (rebuildMaps do protótipo, sem estado global):
 * `areaMap` célula → [areaIds] (ordem de criação; o último é o topo) e
 * `objectMap` célula → {kind: 'rack'|'block'|'fixed', id}.
 */
export function BuildCellMaps(model) {
    const areaMap = new Map();
    const objectMap = new Map();
    for (const area of model.areas) {
        for (let x = area.x1; x <= area.x2; x++) {
            for (let y = area.y1; y <= area.y2; y++) {
                const k = CellKey(x, y);
                if (!areaMap.has(k)) {
                    areaMap.set(k, []);
                }
                areaMap.get(k).push(area.id);
            }
        }
    }
    for (const rack of model.racks) {
        for (const item of rack.sequence) {
            objectMap.set(CellKey(item.x, item.y), { kind: 'rack', id: rack.id });
        }
    }
    for (const block of model.blocks) {
        objectMap.set(CellKey(block.x, block.y), { kind: 'block', id: block.id });
    }
    for (const item of model.fixed || []) {
        objectMap.set(CellKey(item.x, item.y), { kind: 'fixed', id: item.id });
    }
    return { areaMap, objectMap };
}

/** Ids de área na célula (vazio quando nenhuma). */
export function GetAreasAt(maps, x, y) {
    return maps.areaMap.get(CellKey(x, y)) || [];
}

/** Área do TOPO na célula (staging por cima da base). */
export function GetTopAreaAt(maps, x, y) {
    const ids = GetAreasAt(maps, x, y);
    return ids.length ? ids[ids.length - 1] : null;
}

/** Elemento físico na célula ({kind,id} ou null). */
export function GetObjectAt(maps, x, y) {
    return maps.objectMap.get(CellKey(x, y)) || null;
}

/** Área container (que permite rack) contendo TODAS as células, topo primeiro. */
export function FindContainerAreaForCells(model, cells) {
    for (let i = model.areas.length - 1; i >= 0; i--) {
        const area = model.areas[i];
        if (!MetaForArea(area).allowRack) {
            continue;
        }
        if (cells.every((c) => PointInsideRect(c, area))) {
            return area.id;
        }
    }
    return null;
}

/** Maior coordenada ocupada (guarda do encolhimento do grid). */
export function GetMaxOccupied(model) {
    const max = { x: -1, y: -1 };
    const consider = (x, y) => {
        if (Number.isFinite(x) && Number.isFinite(y)) {
            max.x = Math.max(max.x, x);
            max.y = Math.max(max.y, y);
        }
    };
    for (const area of model.areas) {
        consider(area.x1, area.y1);
        consider(area.x2, area.y2);
    }
    for (const rack of model.racks) {
        for (const p of rack.sequence) {
            consider(p.x, p.y);
        }
    }
    for (const block of model.blocks) {
        consider(block.x, block.y);
    }
    for (const item of model.fixed || []) {
        consider(item.x, item.y);
    }
    return max;
}

/**
 * Valida o prefixo do rack real: 1–2 letras, único no layout (é a ponte com
 * `wms_locations.rack`). Retorna a mensagem do problema ou null.
 */
export function ValidateRackPrefix(model, prefix, excludeRackId = null) {
    const normalized = NormalizeRackPrefix(prefix);
    if (!normalized) {
        return 'Informe o prefixo do rack: 1 ou 2 letras (ex.: A, B, AA).';
    }
    const taken = model.racks.some(
        (rack) => rack.id !== excludeRackId && NormalizeRackPrefix(rack.rack) === normalized,
    );
    return taken ? `O rack ${normalized} já está desenhado no mapa — o prefixo do rack é único.` : null;
}

// Sequência de prefixos candidatos: A..Z, depois AA..ZZ (a gramática só aceita
// 2 letras — 702 racks é folga de sobra para qualquer galpão).
function* rackPrefixCandidates() {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    for (const letter of letters) {
        yield letter;
    }
    for (const first of letters) {
        for (const second of letters) {
            yield `${first}${second}`;
        }
    }
}

/**
 * Menor prefixo ainda livre (sugestão de formulário). `taken` são prefixos que
 * já existem no CADASTRO (racks reais sem desenho) — sugerir um deles levaria
 * a um 409 na criação.
 */
export function NextFreeRackPrefix(model, taken = []) {
    const used = new Set(model.racks.map((rack) => NormalizeRackPrefix(rack.rack)).filter(Boolean));
    for (const prefix of taken) {
        const normalized = NormalizeRackPrefix(prefix);
        if (normalized) {
            used.add(normalized);
        }
    }
    for (const candidate of rackPrefixCandidates()) {
        if (!used.has(candidate)) {
            return candidate;
        }
    }
    return null;
}

/**
 * validateWorld do protótipo + regras do prefixo e das fixas. Regras físicas:
 * áreas dentro do grid; duas áreas base (ou duas empilháveis) não se
 * sobrepõem; empilhável sobreposta a base fica TOTALMENTE dentro dela (mas
 * staging SOLTO — fora de qualquer base — é VÁLIDO, 4ª rodada); célula tem no
 * máximo UM elemento físico (corredor, bloco OU fixa); rack/bloco dentro do
 * grid e dentro de área que permite rack; fixa dentro do grid, dentro OU fora
 * de área, com code único.
 * Retorna a mensagem do primeiro problema ou null (mundo válido).
 */
export function ValidateWorld(model) {
    for (const area of model.areas) {
        if (!RectInsideGrid(area, model.grid)) {
            return 'Uma área está fora do grid.';
        }
    }

    for (let i = 0; i < model.areas.length; i++) {
        for (let j = i + 1; j < model.areas.length; j++) {
            const a = model.areas[i];
            const b = model.areas[j];
            if (!RectsOverlap(a, b)) {
                continue;
            }
            const ma = MetaForArea(a);
            const mb = MetaForArea(b);
            if (ma.stackable && mb.stackable) {
                return 'Duas áreas empilháveis não podem se sobrepor.';
            }
            if (!ma.stackable && !mb.stackable) {
                return 'Duas áreas base não podem se sobrepor.';
            }
            const stackable = ma.stackable ? a : b;
            const base = ma.stackable ? b : a;
            if (!RectInside(stackable, base)) {
                return 'Uma área empilhável deve ficar totalmente dentro da área base — ou totalmente fora (staging de transferência).';
            }
        }
    }

    const seenPrefixes = new Set();
    for (const rack of model.racks) {
        const prefix = NormalizeRackPrefix(rack.rack);
        if (!prefix) {
            return `O rack "${rack.name || '?'}" precisa do prefixo real (1 ou 2 letras).`;
        }
        if (seenPrefixes.has(prefix)) {
            return `O prefixo ${prefix} está repetido — cada rack real aparece uma única vez no mapa.`;
        }
        seenPrefixes.add(prefix);
    }

    const occupied = new Map();
    for (const rack of model.racks) {
        const cells = GetAllRackCells(model, rack);
        for (const c of cells) {
            if (!InsideGrid(c.x, c.y, model.grid)) {
                return 'Um rack ou bloco está fora do grid.';
            }
            const k = CellKey(c.x, c.y);
            if (occupied.has(k) && occupied.get(k) !== rack.id) {
                return 'Dois elementos físicos não podem ocupar a mesma posição.';
            }
            occupied.set(k, rack.id);
            const insideAllowedArea = model.areas.some(
                (area) => MetaForArea(area).allowRack && PointInsideRect(c, area),
            );
            if (!insideAllowedArea) {
                return 'Um rack ou bloco está fora de uma área permitida (Logística, Doca ou Produção).';
            }
        }
    }

    // Fixas: 1 célula LIVRE por fixa (nunca sobre corredor/bloco/outra fixa),
    // dentro OU fora de área; code obrigatório e único (é o vínculo real).
    const seenCodes = new Set();
    for (const item of model.fixed || []) {
        const code = String(item.code || '').trim();
        if (!code) {
            return 'Um endereço fixo está sem código — a fixa sempre corresponde a um endereço real.';
        }
        const codeKey = code.toUpperCase();
        if (seenCodes.has(codeKey)) {
            return `O endereço fixo ${code} aparece duas vezes no mapa — cada código entra uma única vez.`;
        }
        seenCodes.add(codeKey);
        if (!InsideGrid(item.x, item.y, model.grid)) {
            return `O endereço fixo ${code} está fora do grid.`;
        }
        const k = CellKey(item.x, item.y);
        if (occupied.has(k)) {
            return `A célula do endereço fixo ${code} já tem outro elemento físico.`;
        }
        occupied.set(k, item.id);
    }

    return null;
}

/*****************************
 * Geometria de rack
 *****************************/

/** Célula vizinha do corredor no lado pedido (dado o eixo do rack). */
export function SideCell(cell, side, orientation) {
    if (orientation === 'vertical') {
        if (side === 'left') {
            return { x: cell.x - 1, y: cell.y };
        }
        if (side === 'right') {
            return { x: cell.x + 1, y: cell.y };
        }
    }
    if (orientation === 'horizontal') {
        if (side === 'top') {
            return { x: cell.x, y: cell.y + 1 };
        }
        if (side === 'bottom') {
            return { x: cell.x, y: cell.y - 1 };
        }
    }
    return { x: cell.x, y: cell.y };
}

/**
 * De que lado do corredor os blocos podem ficar, por eixo. É GEOMETRIA do
 * desenho (onde a fileira de vãos é desenhada em relação ao corredor), não
 * paridade: o primeiro da lista é o default do painel. Convenção de
 * coordenadas do mapa: eixo X 0=ESQUERDA, eixo Y 0=embaixo/SAÍDA, cresce para o
 * FUNDO — por isso `bottom` é y−1 e `top` é y+1.
 */
export const BAY_SIDES_BY_ORIENTATION = { vertical: ['right', 'left'], horizontal: ['bottom', 'top'] };

/**
 * Lado coerente com o eixo: rack vertical só tem direita/esquerda, horizontal
 * só tem baixo/cima. Como a orientação é DERIVADA do traçado, o lado escolhido
 * no painel pode ficar do eixo errado depois de um novo traçado — aqui ele
 * volta pro par certo (right↔bottom, left↔top), preservando "a mão".
 */
export function CoerceBaySideForOrientation(orientation, side) {
    const options = BAY_SIDES_BY_ORIENTATION[orientation] || BAY_SIDES_BY_ORIENTATION.vertical;
    if (options.includes(side)) {
        return side;
    }
    const isSecond = side === 'left' || side === 'top';
    return options[isSecond ? 1 : 0];
}

/**
 * Eixo DERIVADO do traçado de dois cliques (regra do dono):
 * `|dx| >= |dy|` → horizontal, senão vertical; `length` = distância no eixo
 * dominante + 1 (a célula do próprio clique). Um clique duplo na MESMA célula
 * (dx = dy = 0) cai no `>=` e vira horizontal de 1 célula — regra literal, sem
 * exceção escondida. A ordem dos cliques não muda o EIXO nem o comprimento (só
 * o módulo da distância conta aqui); quem numera A PARTIR do 1º clique (Bloco
 * 01 no start) é a `ComputeRackGeometry`.
 */
export function DeriveRackAxis(start, end) {
    const dx = Math.abs(end.x - start.x);
    const dy = Math.abs(end.y - start.y);
    const orientation = dx >= dy ? 'horizontal' : 'vertical';
    return { orientation, length: (orientation === 'horizontal' ? dx : dy) + 1 };
}

/** Nº de blocos do rack: inteiro 1..RACK_REAL_MAX_BAYS; vazio/inválido cai no
 *  `fallback` (o comprimento do traçado). */
function clampBays(value, fallback) {
    if (value === null || value === undefined || String(value).trim() === '') {
        return fallback;
    }
    const n = Math.round(Number(value));
    if (!Number.isFinite(n) || n < 1) {
        return fallback;
    }
    return Math.min(n, RACK_REAL_MAX_BAYS);
}

/**
 * Geometria do corredor entre os dois cliques. REGRA DO DONO (fix do Bloco 01):
 * o rack COMEÇA no 1º clique (`start`) e caminha rumo ao 2º (`end`),
 * RESPEITANDO A DIREÇÃO desenhada — subindo/descendo, esquerda↔direita. Assim
 * o ordinal 1 (→ Bloco 01) fica na célula do 1º clique. O eixo secundário é
 * achatado no valor do INÍCIO (traçado torto vira reta).
 *
 * `opts.bays` (quantos blocos) manda no comprimento do corredor quando
 * informado: o corredor estende a partir do `start` NA DIREÇÃO desenhada até
 * ter um corredor por bloco; em branco, o comprimento é o do traçado. O lado
 * dos blocos (`opts.baySide`) é coagido ao eixo derivado.
 */
export function ComputeRackGeometry(start, end, opts) {
    if (!start || !end) {
        return null;
    }
    const { orientation } = DeriveRackAxis(start, end);
    const baySide = CoerceBaySideForOrientation(orientation, opts.baySide);

    if (orientation === 'vertical') {
        const x = start.x;
        // Direção do TRAÇADO no eixo dominante: +1 se o 2º clique está acima do
        // 1º, -1 se abaixo. Vertical ⇒ |dy| > |dx| ⇒ start.y ≠ end.y (passo ≠ 0).
        const step = end.y >= start.y ? 1 : -1;
        const traceLen = Math.abs(end.y - start.y) + 1;
        const bays = clampBays(opts.bays, traceLen);
        const sequence = [];
        for (let i = 0; i < bays; i += 1) {
            sequence.push({ x, y: start.y + i * step, bayId: null });
        }
        return {
            orientation: 'vertical',
            dir: { dx: 0, dy: step },
            start: { x, y: start.y },
            end: { x, y: start.y + (bays - 1) * step },
            sequence,
            baySide,
            bays,
        };
    }

    const y = start.y;
    const step = end.x >= start.x ? 1 : -1;
    const traceLen = Math.abs(end.x - start.x) + 1;
    const bays = clampBays(opts.bays, traceLen);
    const sequence = [];
    for (let i = 0; i < bays; i += 1) {
        sequence.push({ x: start.x + i * step, y, bayId: null });
    }
    return {
        orientation: 'horizontal',
        dir: { dx: step, dy: 0 },
        start: { x: start.x, y },
        end: { x: start.x + (bays - 1) * step, y },
        sequence,
        baySide,
        bays,
    };
}

function rackDir(rack) {
    return rack.dir || (rack.orientation === 'vertical' ? { dx: 0, dy: -1 } : { dx: 1, dy: 0 });
}

/**
 * Novo fim do rack a partir da célula arrastada: a alça só estende NO EIXO e SÓ
 * na DIREÇÃO do traçado (`rack.dir`) — o fim nunca cruza para o outro lado do
 * `start`. Como o start é o 1º clique, estender/encurtar preserva o Bloco 01 na
 * origem.
 */
export function ComputeRackResizeEnd(rack, currentCell) {
    const dir = rackDir(rack);
    if (rack.orientation === 'vertical') {
        const steps = Math.max(0, (currentCell.y - rack.start.y) * dir.dy);
        return { x: rack.start.x, y: rack.start.y + steps * dir.dy };
    }
    const steps = Math.max(0, (currentCell.x - rack.start.x) * dir.dx);
    return { x: rack.start.x + steps * dir.dx, y: rack.start.y };
}

function rackCoordsBetween(rack, newEnd) {
    const dir = rackDir(rack);
    const coords = [];
    if (rack.orientation === 'vertical') {
        const steps = Math.abs(newEnd.y - rack.start.y);
        for (let i = 0; i <= steps; i += 1) {
            coords.push({ x: rack.start.x, y: rack.start.y + i * dir.dy });
        }
    } else {
        const steps = Math.abs(newEnd.x - rack.start.x);
        for (let i = 0; i <= steps; i += 1) {
            coords.push({ x: rack.start.x + i * dir.dx, y: rack.start.y });
        }
    }
    return coords;
}

/** Células do PREVIEW do redimensionamento (corredor + blocos). */
export function ComputeRackResizePreviewCells(rack, currentCell) {
    if (!currentCell) {
        return [];
    }
    const newEnd = ComputeRackResizeEnd(rack, currentCell);
    const cells = [];
    for (const c of rackCoordsBetween(rack, newEnd)) {
        cells.push(c);
        cells.push(SideCell(c, rack.baySide, rack.orientation));
    }
    return cells;
}

/** Bounds do resize de área por alça (n/s/e/w e diagonais). */
export function ComputeAreaResizeBounds(original, handle, cell) {
    let x1 = original.x1;
    let x2 = original.x2;
    let y1 = original.y1;
    let y2 = original.y2;
    if (handle.includes('w')) {
        x1 = cell.x;
    }
    if (handle.includes('e')) {
        x2 = cell.x;
    }
    if (handle.includes('s')) {
        y1 = cell.y;
    }
    if (handle.includes('n')) {
        y2 = cell.y;
    }
    return NormalizeBounds(x1, y1, x2, y2);
}

/*****************************
 * Viewport (canvas)
 *****************************/

/**
 * Transformação de desenho (sem DOM): `fitBounds` null → tamanho natural
 * (célula do layout, editor com rolagem); com bounds → o RECORTE indicado é
 * ajustado ao espaço disponível (visão por área do editor e o
 * `fit="viewport"` do modo status — bounds = grid inteiro cabendo no
 * container, sem rolagem). `margin` é a moldura em px (o editor usa
 * MAP_MARGIN para as réguas; os modos de leitura usam uma moldura fina).
 * Y cresce para CIMA (offsetY é a linha de base inferior).
 */
export function ComputeViewTransform(grid, fitBounds, availWidth, availHeight, margin = MAP_MARGIN) {
    if (fitBounds) {
        const availW = Math.max(120, (availWidth || 800) - margin * 2);
        const availH = Math.max(120, (availHeight || 600) - margin * 2);
        const cellsW = fitBounds.x2 - fitBounds.x1 + 1;
        const cellsH = fitBounds.y2 - fitBounds.y1 + 1;
        const cell = clampNumber(Math.floor(Math.min(availW / cellsW, availH / cellsH)), 3, 80);
        const canvasW = cellsW * cell + margin * 2;
        const canvasH = cellsH * cell + margin * 2;
        return {
            mode: 'fit',
            cell,
            margin,
            offsetX: margin - fitBounds.x1 * cell,
            offsetY: canvasH - margin + fitBounds.y1 * cell,
            canvasW,
            canvasH,
            bounds: { ...fitBounds },
        };
    }

    const cell = grid?.cell || 28;
    const w = grid?.w || 30;
    const h = grid?.h || 30;
    const canvasW = margin * 2 + w * cell;
    const canvasH = margin * 2 + h * cell;
    return {
        mode: 'natural',
        cell,
        margin,
        offsetX: margin,
        offsetY: canvasH - margin,
        canvasW,
        canvasH,
        bounds: { x1: 0, y1: 0, x2: w - 1, y2: h - 1 },
    };
}

/** Retângulo em px da célula [px, py, w, h]. */
export function CellScreenRect(view, x, y) {
    const cell = view.cell;
    return [view.offsetX + x * cell, view.offsetY - (y + 1) * cell, cell, cell];
}

/** Célula visível na view (respeita bounds da visão de área). */
export function InsideView(view, x, y) {
    const b = view.bounds;
    return x >= b.x1 && y >= b.y1 && x <= b.x2 && y <= b.y2;
}

/** Pixel → célula (null fora da view). */
export function ScreenToCell(view, px, py) {
    if (!view) {
        return null;
    }
    const x = Math.floor((px - view.offsetX) / view.cell);
    const y = Math.floor((view.offsetY - py) / view.cell);
    return InsideView(view, x, y) ? { x, y } : null;
}

/** Retângulo em px da área. */
export function AreaScreenRect(view, area) {
    const [px, py] = CellScreenRect(view, area.x1, area.y2);
    return {
        x: px,
        y: py,
        w: (area.x2 - area.x1 + 1) * view.cell,
        h: (area.y2 - area.y1 + 1) * view.cell,
    };
}

/** Posições das 8 alças da área [[nome, x, y], ...]. */
export function AreaHandleScreenPositions(view, area) {
    const r = AreaScreenRect(view, area);
    return [
        ['nw', r.x, r.y],
        ['n', r.x + r.w / 2, r.y],
        ['ne', r.x + r.w, r.y],
        ['e', r.x + r.w, r.y + r.h / 2],
        ['se', r.x + r.w, r.y + r.h],
        ['s', r.x + r.w / 2, r.y + r.h],
        ['sw', r.x, r.y + r.h],
        ['w', r.x, r.y + r.h / 2],
    ];
}

/** Centro em px da alça de fim do rack. */
export function RackHandleScreenPos(view, rack) {
    const [px, py, cw, ch] = CellScreenRect(view, rack.end.x, rack.end.y);
    return [px + cw / 2, py + ch / 2];
}

/**
 * Alça sob o cursor DA SELEÇÃO ATUAL (interação direta da r3: as alças só
 * existem — e só capturam o ponteiro — no elemento selecionado; rack = alça no
 * fim (raio 14px), área = 8 alças na borda (raio 11px)).
 */
export function GetSelectionHandleAt(model, view, selection, px, py) {
    if (!selection) {
        return null;
    }
    if (selection.kind === 'rack') {
        const rack = GetRackById(model, selection.id);
        if (rack) {
            const [hx, hy] = RackHandleScreenPos(view, rack);
            if (Math.hypot(px - hx, py - hy) <= 14) {
                return { mode: 'resize-rack', rackId: rack.id, handle: 'end' };
            }
        }
        return null;
    }
    if (selection.kind === 'area') {
        const area = GetAreaById(model, selection.id);
        if (area) {
            for (const [name, hx, hy] of AreaHandleScreenPositions(view, area)) {
                if (Math.hypot(px - hx, py - hy) <= 11) {
                    return { mode: 'resize-area', areaId: area.id, handle: name };
                }
            }
        }
    }
    return null;
}

/*****************************
 * Overlay de status (modo status)
 *****************************/

/**
 * Indexa as linhas do GET /locations/map (já planas via ParseLocationsMapRows:
 * `[{rack, bay, total, active, blocked}]`) para o vínculo com o desenho:
 * `blocks` por BlockKey(rack, bay) e agregado por rack (`racks`), incluindo a
 * lista de vãos vivos de cada rack — é ela que posiciona um rack que existe no
 * cadastro mas ainda não foi desenhado.
 */
export function BuildStatusOverlayIndex(rows) {
    const blocks = new Map();
    const racks = new Map();
    for (const row of rows || []) {
        const rack = NormalizeRackPrefix(row?.rack);
        const bay = Number(row?.bay);
        if (!rack || !Number.isInteger(bay) || bay < 1) {
            continue;
        }
        blocks.set(BlockKey(rack, bay), { ...row, rack, bay });
        if (!racks.has(rack)) {
            racks.set(rack, { rack, total: 0, active: 0, blocked: 0, bays: [] });
        }
        const agg = racks.get(rack);
        agg.total += Number(row.total) || 0;
        agg.active += Number(row.active) || 0;
        agg.blocked += Number(row.blocked) || 0;
        if (!agg.bays.includes(bay)) {
            agg.bays.push(bay);
        }
    }
    for (const agg of racks.values()) {
        agg.bays.sort((a, b) => a - b);
    }
    return { blocks, racks };
}

/*****************************
 * Operações de edição ({ ok, model, error, selection? })
 *****************************/

// Snapshot/rollback do protótipo em forma pura: clona, muta a cópia, valida o
// mundo; erro em qualquer etapa devolve o modelo ORIGINAL + mensagem.
function applyLayoutMutation(model, mutator) {
    const next = CloneLayout(model);
    const failure = mutator(next);
    if (typeof failure === 'string' && failure) {
        return { ok: false, model, error: failure };
    }
    const error = ValidateWorld(next);
    if (error) {
        return { ok: false, model, error };
    }
    return { ok: true, model: next, error: null };
}

/**
 * Retângulo default do DROP de área (paleta → canvas): AREA_DROP_SIZE
 * centrado na célula solta, deslizado para dentro do grid quando encosta na
 * borda (nunca clipa — o tamanho só encolhe se o grid for menor que ele).
 */
export function ComputeAreaDropRect(grid, cell, size = AREA_DROP_SIZE) {
    const w = Math.min(size.w, grid.w);
    const h = Math.min(size.h, grid.h);
    const x1 = clampNumber(cell.x - Math.floor((w - 1) / 2), 0, grid.w - w);
    const y1 = clampNumber(cell.y - Math.floor((h - 1) / 2), 0, grid.h - h);
    return { x1, y1, x2: x1 + w - 1, y2: y1 + h - 1 };
}

/**
 * Cria a área do DROP da paleta: tenta o retângulo default; se o mundo
 * recusar (sobreposição, staging fora de base…), tenta 1×1 exatamente na
 * célula solta — o drop é tolerante, o erro só aparece quando nem a célula
 * única cabe ali.
 */
export function CreateAreaAtCell(model, cell, type) {
    const rect = ComputeAreaDropRect(model.grid, cell);
    const attempt = CreateAreaInLayout(model, { x: rect.x1, y: rect.y1 }, { x: rect.x2, y: rect.y2 }, type);
    if (attempt.ok) {
        return attempt;
    }
    return CreateAreaInLayout(model, cell, cell, type);
}

/**
 * Fim do rack a partir do início + comprimento do corredor (células): rack
 * vertical nasce no ponto solto e DESCE; horizontal segue à DIREITA — a mesma
 * direção de crescimento da alça de resize. A UI NÃO usa isto (o fim vem do 2º
 * clique) — sobra para criação PROGRAMÁTICA (seed), que tem comprimento e não
 * tem cursor. Atenção ao comprimento 1: o par início/fim fica na mesma célula
 * e `DeriveRackAxis` classifica como horizontal (regra literal do dono).
 */
export function ComputeRackEndForLength(start, orientation, length) {
    const len = Math.max(1, Math.round(Number(length) || 1));
    if (orientation === 'vertical') {
        return { x: start.x, y: start.y - (len - 1) };
    }
    return { x: start.x + (len - 1), y: start.y };
}

/** Bloco desenhado que casa rack real + nº do bloco (highlight da lista). */
export function FindBlockByRackBay(model, rackPrefix, bay) {
    const rack = FindRackByPrefix(model, rackPrefix);
    if (!rack) {
        return null;
    }
    const wanted = Number(bay);
    return model.blocks.find((block) => block.rackId === rack.id && Number(block.bay) === wanted) || null;
}

/** Cria área tipo `type` entre dois cantos. */
export function CreateAreaInLayout(model, start, end, type) {
    const r = NormRect(start, end);
    if (!RectInsideGrid(r, model.grid)) {
        return { ok: false, model, error: 'A área está fora do grid.' };
    }
    const area = { id: Uid(), type, x1: r.x1, y1: r.y1, x2: r.x2, y2: r.y2 };
    const result = applyLayoutMutation(model, (next) => {
        next.areas.push(area);
    });
    return result.ok ? { ...result, selection: { kind: 'area', id: area.id } } : result;
}

/** Troca o tipo da área. */
export function UpdateAreaTypeInLayout(model, areaId, type) {
    return applyLayoutMutation(model, (next) => {
        const area = GetAreaById(next, areaId);
        if (!area) {
            return 'Área não encontrada.';
        }
        area.type = type;
    });
}

/**
 * Remove a área + áreas contidas nela. Racks NUNCA vão junto (rack com prefixo
 * é dado real — não existe exclusão em cascata): área que abriga qualquer
 * célula de rack não pode ser excluída (mova os racks antes). Fixas não
 * bloqueiam nem somem — vivem dentro ou fora de área.
 */
export function DeleteAreaFromLayout(model, areaId) {
    const area = GetAreaById(model, areaId);
    if (!area) {
        return { ok: false, model, error: 'Área não encontrada.' };
    }
    for (const rack of model.racks) {
        const touches =
            rack.areaId === areaId || GetAllRackCells(model, rack).some((c) => PointInsideRect(c, area));
        if (touches) {
            return {
                ok: false,
                model,
                error: 'Esta área contém racks com endereços reais — mova os racks para outra área antes de excluí-la. Excluir racks pelo Delete da área não é permitido.',
            };
        }
    }
    const areaIdsToDelete = new Set([areaId]);
    for (const other of model.areas) {
        if (other.id !== areaId && RectInside(other, area)) {
            areaIdsToDelete.add(other.id);
        }
    }
    return applyLayoutMutation(model, (next) => {
        next.areas = next.areas.filter((a) => !areaIdsToDelete.has(a.id));
    });
}

/** Move a área (com áreas e racks contidos) por dx/dy. */
export function MoveAreaInLayout(model, areaId, dx, dy) {
    const area = GetAreaById(model, areaId);
    if (!area) {
        return { ok: false, model, error: 'Área não encontrada.' };
    }
    if (!dx && !dy) {
        return { ok: true, model, error: null };
    }
    const oldRect = { x1: area.x1, y1: area.y1, x2: area.x2, y2: area.y2 };
    const movedAreaIds = new Set([areaId]);
    for (const other of model.areas) {
        if (other.id !== areaId && RectInside(other, oldRect)) {
            movedAreaIds.add(other.id);
        }
    }
    const movedRackIds = new Set();
    for (const rack of model.racks) {
        const belongsToMovedArea = movedAreaIds.has(rack.areaId);
        const fullyInside = GetAllRackCells(model, rack).every((c) => PointInsideRect(c, oldRect));
        if (belongsToMovedArea || (!rack.areaId && fullyInside)) {
            movedRackIds.add(rack.id);
        }
    }
    // Fixas dentro do retângulo movido vão junto (associação à área é
    // geométrica/dinâmica — mover a área carrega o que está dentro dela).
    const movedFixedIds = new Set(
        (model.fixed || []).filter((item) => PointInsideRect(item, oldRect)).map((item) => item.id),
    );
    return applyLayoutMutation(model, (next) => {
        for (const id of movedAreaIds) {
            const a = GetAreaById(next, id);
            a.x1 += dx;
            a.x2 += dx;
            a.y1 += dy;
            a.y2 += dy;
        }
        for (const rackId of movedRackIds) {
            const rack = GetRackById(next, rackId);
            rack.start.x += dx;
            rack.start.y += dy;
            rack.end.x += dx;
            rack.end.y += dy;
            for (const item of rack.sequence) {
                item.x += dx;
                item.y += dy;
            }
            for (const block of GetRackBlocks(next, rackId)) {
                block.x += dx;
                block.y += dy;
            }
            if (!rack.areaId) {
                rack.areaId = areaId;
            }
        }
        for (const item of next.fixed || []) {
            if (movedFixedIds.has(item.id)) {
                item.x += dx;
                item.y += dy;
            }
        }
    });
}

/** Redimensiona a área pela alça. */
export function ResizeAreaInLayout(model, areaId, originalRect, handle, cell) {
    const newRect = ComputeAreaResizeBounds(originalRect, handle, cell);
    return applyLayoutMutation(model, (next) => {
        const area = GetAreaById(next, areaId);
        if (!area) {
            return 'Área não encontrada.';
        }
        area.x1 = newRect.x1;
        area.y1 = newRect.y1;
        area.x2 = newRect.x2;
        area.y2 = newRect.y2;
    });
}

function buildBlockForCell(rack, item, bay) {
    const bc = SideCell(item, rack.baySide, rack.orientation);
    return {
        id: Uid(),
        rackId: rack.id,
        x: bc.x,
        y: bc.y,
        bay,
        width: rack.params?.blockWidth ?? RACK_DRAFT_DEFAULTS.blockWidth,
        maxLevels: rack.params?.maxLevels ?? RACK_DRAFT_DEFAULTS.maxLevels,
    };
}

/**
 * Cria o rack com os blocos SEQUENCIAIS (1..N ao longo do traçado, a partir do
 * 1º clique) do lado `baySide` do corredor. `opts` = shape de
 * RACK_DRAFT_DEFAULTS + `rack` (prefixo) validado e único.
 *
 * `start`/`end` são os DOIS CLIQUES do traçado — orientação e comprimento saem
 * daí (`ComputeRackGeometry`), nunca de `opts`. O que o rack guarda
 * (`orientation`, `baySide`, `start`, `end`, `dir`) é sempre o que a geometria
 * decidiu.
 *
 * `existingBays` (Set de nºs) é o caso "posicionar um rack que JÁ existe no
 * cadastro": só os vãos vivos ganham bloco no desenho; os demais ficam como gap
 * (célula do corredor sem `bayId`). Sem `existingBays`, todos os N ganham bloco.
 */
export function CreateRackInLayout(model, start, end, opts, { existingBays = null } = {}) {
    const prefixError = ValidateRackPrefix(model, opts.rack);
    if (prefixError) {
        return { ok: false, model, error: prefixError };
    }
    const prefix = NormalizeRackPrefix(opts.rack);
    const geom = ComputeRackGeometry(start, end, opts);
    if (!geom) {
        return { ok: false, model, error: 'Não foi possível gerar o rack.' };
    }

    const rack = {
        id: Uid(),
        areaId: null,
        name: String(opts.name || '').trim() || `Rack ${prefix}`,
        rack: prefix,
        orientation: geom.orientation,
        baySide: geom.baySide,
        start: geom.start,
        end: geom.end,
        dir: geom.dir,
        sequence: geom.sequence,
        params: {
            levels: opts.levels,
            maxLevels: opts.maxLevels,
            levelHeight: opts.levelHeight,
            positions: opts.positions,
            blockWidth: opts.blockWidth,
        },
    };

    const newBlocks = [];
    for (const [index, item] of rack.sequence.entries()) {
        const bay = index + 1;
        if (existingBays && !existingBays.has(bay)) {
            continue;
        }
        const block = buildBlockForCell(rack, item, bay);
        newBlocks.push(block);
        item.bayId = block.id;
    }

    const allCells = [];
    for (const item of rack.sequence) {
        allCells.push({ x: item.x, y: item.y });
        allCells.push(SideCell(item, rack.baySide, rack.orientation));
    }
    rack.areaId = FindContainerAreaForCells(model, allCells);

    const result = applyLayoutMutation(model, (next) => {
        next.racks.push(rack);
        next.blocks.push(...newBlocks);
    });
    return result.ok ? { ...result, selection: { kind: 'rack', id: rack.id } } : result;
}

/**
 * Remove o RACK e todos os seus blocos do LAYOUT (desenho). Diferente das
 * outras operações NÃO valida nem trava: quem chama é o fluxo de exclusão do
 * editor, DEPOIS de o backend ter soft-deletado (ou confirmado 404) os endereços
 * reais — aqui é só tirar do desenho. Idempotente. Devolve o NOVO layout.
 */
export function RemoveRackFromLayout(model, rackId) {
    const next = CloneLayout(model);
    next.racks = next.racks.filter((rack) => rack.id !== rackId);
    next.blocks = next.blocks.filter((block) => block.rackId !== rackId);
    return next;
}

/**
 * Remove UM BLOCO do LAYOUT (desenho): tira o bloco e limpa a referência dele
 * na sequência do corredor (vira um gap). Sem trava (o backend já soft-deletou
 * o vão). Idempotente. Devolve o NOVO layout.
 */
export function RemoveBlockFromLayout(model, blockId) {
    const next = CloneLayout(model);
    const block = GetBlockById(next, blockId);
    if (!block) {
        return next;
    }
    next.blocks = next.blocks.filter((item) => item.id !== blockId);
    const rack = GetRackById(next, block.rackId);
    if (rack) {
        for (const item of rack.sequence) {
            if (item.bayId === blockId) {
                item.bayId = null;
            }
        }
    }
    return next;
}

/**
 * Adiciona UM BLOCO (pelo NÚMERO) ao LAYOUT, preenchendo um GAP do corredor. O
 * número É o ordinal ao longo do traçado (Bloco N = N-ésima célula do
 * corredor, a partir do 1º clique) — o MESMO mapeamento célula↔número do
 * gerador do backend (sequencial). Valida o mundo (fora do grid / célula
 * ocupada => erro). Devolve { ok, model, error }.
 */
export function AddBlockToLayout(model, rackId, bay) {
    const rack = GetRackById(model, rackId);
    if (!rack) {
        return { ok: false, model, error: 'Rack não encontrado.' };
    }
    const seqIndex = Number(bay) - 1;
    if (!Number.isInteger(seqIndex) || seqIndex < 0 || seqIndex >= rack.sequence.length) {
        return { ok: false, model, error: 'Esse bloco fica fora do corredor do rack.' };
    }
    if (rack.sequence[seqIndex].bayId) {
        return { ok: false, model, error: 'Esse bloco já existe no rack.' };
    }
    return applyLayoutMutation(model, (next) => {
        const target = GetRackById(next, rackId);
        if (!target) {
            return 'Rack não encontrado.';
        }
        const item = target.sequence[seqIndex];
        const block = buildBlockForCell(target, item, Number(bay));
        item.bayId = block.id;
        next.blocks.push(block);
        return null;
    });
}

/**
 * Reconhece uma célula VAZIA como posição ADICIONÁVEL de bloco (o gap): se
 * `cell` é a célula do lado dos blocos (`SideCell`) de alguma célula do
 * corredor de um rack e NÃO tem bloco ali, devolve { rackId, rack, bay } — o
 * número é o ordinal na sequência, batendo com o backend. Célula ocupada => null.
 */
export function FindAddableBlockAt(model, cell) {
    if (!cell) {
        return null;
    }
    const maps = BuildCellMaps(model);
    if (GetObjectAt(maps, cell.x, cell.y)) {
        return null;
    }
    for (const rack of model.racks) {
        for (let i = 0; i < rack.sequence.length; i += 1) {
            const item = rack.sequence[i];
            if (item.bayId) {
                continue;
            }
            const bc = SideCell(item, rack.baySide, rack.orientation);
            if (bc.x === cell.x && bc.y === cell.y) {
                return { rackId: rack.id, rack: rack.rack, bay: i + 1 };
            }
        }
    }
    return null;
}

/** Atualiza nome e prefixo real do rack. */
export function UpdateRackInLayout(model, rackId, { name, rack: prefix }) {
    const prefixError = ValidateRackPrefix(model, prefix, rackId);
    if (prefixError) {
        return { ok: false, model, error: prefixError };
    }
    return applyLayoutMutation(model, (next) => {
        const rack = GetRackById(next, rackId);
        if (!rack) {
            return 'Rack não encontrado.';
        }
        const normalized = NormalizeRackPrefix(prefix);
        rack.name = String(name || '').trim() || `Rack ${normalized}`;
        rack.rack = normalized;
    });
}

/** Move o rack inteiro (corredor + blocos) por dx/dy. */
export function MoveRackInLayout(model, rackId, dx, dy) {
    if (!dx && !dy) {
        return { ok: true, model, error: null };
    }
    return applyLayoutMutation(model, (next) => {
        const rack = GetRackById(next, rackId);
        if (!rack) {
            return 'Rack não encontrado.';
        }
        rack.start.x += dx;
        rack.start.y += dy;
        rack.end.x += dx;
        rack.end.y += dy;
        for (const item of rack.sequence) {
            item.x += dx;
            item.y += dy;
        }
        for (const block of GetRackBlocks(next, rackId)) {
            block.x += dx;
            block.y += dy;
        }
        rack.areaId = FindContainerAreaForCells(next, GetAllRackCells(next, rack));
    });
}

/**
 * Estende/reduz o rack pela alça de fim, preservando os blocos (e a numeração)
 * das células mantidas; células novas ganham bloco com o número SEQUENCIAL da
 * posição (N-ésima célula → Bloco N), que é o que o backend gera no add-bay.
 */
export function ResizeRackInLayout(model, rackId, currentCell) {
    if (!currentCell) {
        return { ok: false, model, error: 'Não foi possível redimensionar o rack.' };
    }
    return applyLayoutMutation(model, (next) => {
        const rack = GetRackById(next, rackId);
        if (!rack) {
            return 'Rack não encontrado.';
        }
        const newEnd = ComputeRackResizeEnd(rack, currentCell);
        const newCoords = rackCoordsBetween(rack, newEnd);
        if (!newCoords.length) {
            return 'Não foi possível redimensionar o rack.';
        }

        const oldSeq = rack.sequence;
        const newSequence = newCoords.map((c, index) => ({
            x: c.x,
            y: c.y,
            bayId: index < oldSeq.length ? (oldSeq[index].bayId ?? null) : null,
        }));
        const preservedBlockIds = new Set(newSequence.map((item) => item.bayId).filter(Boolean));
        const removedSet = new Set(
            GetRackBlocks(next, rackId)
                .map((block) => block.id)
                .filter((blockId) => !preservedBlockIds.has(blockId)),
        );

        const newBlocks = [];
        for (const [index, item] of newSequence.entries()) {
            // Só as células NOVAS ganham bloco; um gap preservado continua gap
            // (o dono apagou aquele vão de propósito).
            if (index < oldSeq.length) {
                continue;
            }
            const block = buildBlockForCell(rack, item, index + 1);
            newBlocks.push(block);
            item.bayId = block.id;
        }

        next.blocks = next.blocks.filter((block) => !removedSet.has(block.id));
        next.blocks.push(...newBlocks);
        rack.sequence = newSequence;
        rack.end = newEnd;
        rack.areaId = FindContainerAreaForCells(next, GetAllRackCells(next, rack));
    });
}

/** Atualiza largura (m) e máximo de andares do bloco. */
export function UpdateBlockInLayout(model, blockId, { width, maxLevels }) {
    return applyLayoutMutation(model, (next) => {
        const block = GetBlockById(next, blockId);
        if (!block) {
            return 'Bloco não encontrado.';
        }
        block.width = width;
        block.maxLevels = maxLevels;
    });
}

/*****************************
 * Endereços fixos no layout (4ª rodada)
 *****************************/

/**
 * Posiciona um endereço fixo REAL (DCA/STG-DCA/FAB/INC) numa célula livre —
 * 1 célula por fixa, dentro OU fora de área. `code` é o vínculo com o
 * endereço literal; a validação do mundo garante célula livre e code único.
 */
export function CreateFixedInLayout(model, cell, code) {
    const cleanCode = String(code || '')
        .trim()
        .toUpperCase();
    if (!cleanCode) {
        return { ok: false, model, error: 'Informe o código do endereço fixo.' };
    }
    if (!InsideGrid(cell.x, cell.y, model.grid)) {
        return { ok: false, model, error: 'A célula está fora do grid.' };
    }
    const fixedItem = { id: Uid(), code: cleanCode, x: cell.x, y: cell.y };
    const result = applyLayoutMutation(model, (next) => {
        if (!Array.isArray(next.fixed)) {
            next.fixed = [];
        }
        next.fixed.push(fixedItem);
    });
    return result.ok ? { ...result, selection: { kind: 'fixed', id: fixedItem.id } } : result;
}

/** Move a fixa por dx/dy (arrasto livre; o mundo valida a célula destino). */
export function MoveFixedInLayout(model, fixedId, dx, dy) {
    if (!dx && !dy) {
        return { ok: true, model, error: null };
    }
    return applyLayoutMutation(model, (next) => {
        const item = GetFixedById(next, fixedId);
        if (!item) {
            return 'Endereço fixo não encontrado.';
        }
        item.x += dx;
        item.y += dy;
    });
}

/**
 * Aplica w/h/célula do grid com a guarda do protótipo: não encolhe além do
 * conteúdo (corrige para o mínimo e devolve `warning` — aplicado mesmo assim).
 */
export function SetGridInLayout(model, gridInput) {
    const max = GetMaxOccupied(model);
    let warning = null;
    let w = clampInt(gridInput.w, GRID_LIMITS.minSize, GRID_LIMITS.maxSize, model.grid.w);
    let h = clampInt(gridInput.h, GRID_LIMITS.minSize, GRID_LIMITS.maxSize, model.grid.h);
    const cell = clampInt(gridInput.cell, GRID_LIMITS.minCell, GRID_LIMITS.maxCell, model.grid.cell);
    if (w <= max.x) {
        w = max.x + 1;
        warning = 'O grid não pode encolher além do conteúdo existente.';
    }
    if (h <= max.y) {
        h = max.y + 1;
        warning = 'O grid não pode encolher além do conteúdo existente.';
    }
    const result = applyLayoutMutation(model, (next) => {
        next.grid = { w, h, cell };
    });
    return result.ok ? { ...result, warning } : result;
}
